import axios from '../../api/axios';
import { toast } from 'react-toastify';

/**
 * API endpoints for MROSupplier operations
 */
export const ENDPOINTS = {
  GET_ALL_MRO_SUPPLIERS: 'v2/mro-suppliers/get-all',
  CREATE_MRO_SUPPLIER: 'v2/mro-suppliers/create',
  UPDATE_MRO_SUPPLIER: 'v2/mro-suppliers/update-supplier/:id',
  DELETE_MRO_SUPPLIER: 'v2/mro-suppliers/delete/:id',
  GENERATE_OTP: 'v2/mro-suppliers/generate-otp/:id',
  GENERATE_PASSWORD_RESET: 'v2/mro-suppliers/generate-password-reset/:id',
};

/**
 * Get authentication headers
 * @returns {Object} Headers with Bearer token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 */
const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || error.message || defaultMessage;
  console.error(defaultMessage, error);
  throw new Error(message);
};

/**
 * Fetches all MRO suppliers
 * @returns {Promise<Object>} List of MRO suppliers
 */
export const getAllMROSuppliers = async () => {
  try {
    const response = await axios.get(ENDPOINTS.GET_ALL_MRO_SUPPLIERS, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch MRO suppliers');
  }
};

/**
 * Creates a new MRO supplier
 * @param {Object} payload - MRO supplier data
 * @returns {Promise<Object>} Created MRO supplier
 */
export const createMROSupplier = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE_MRO_SUPPLIER, payload, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create MRO supplier');
  }
};

/**
 * Updates an existing MRO supplier
 * @param {string} id - MRO supplier ID
 * @param {Object} payload - Updated MRO supplier data
 * @returns {Promise<Object>} Updated MRO supplier
 */
export const updateMROSupplier = async (id, payload) => {
  try {
    const url = ENDPOINTS.UPDATE_MRO_SUPPLIER.replace(':id', id);
    const response = await axios.post(url, payload, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update MRO supplier');
  }
};

/**
 * Deletes an MRO supplier
 * @param {string} id - MRO supplier ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteMROSupplier = async (id) => {
  try {
    const url = ENDPOINTS.DELETE_MRO_SUPPLIER.replace(':id', id);
    const response = await axios.delete(url, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete MRO supplier');
  }
};

/**
 * Generates OTP for an MRO supplier
 * @param {string} id - MRO supplier ID
 * @returns {Promise<Object>} Updated MRO supplier with OTP
 */
export const generateOTP = async (id) => {
  try {
    const url = ENDPOINTS.GENERATE_OTP.replace(':id', id);
    const response = await axios.post(url, {}, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to generate OTP');
  }
};

/**
 * Generates password reset token for an MRO supplier
 * @param {string} id - MRO supplier ID
 * @returns {Promise<Object>} Updated MRO supplier with password reset token
 */
export const generatePasswordReset = async (id) => {
  try {
    const url = ENDPOINTS.GENERATE_PASSWORD_RESET.replace(':id', id);
    const response = await axios.post(url, {}, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to generate password reset token');
  }
};